import { useState } from "react";
import Modal from "../ui/Modal";

const PaymentHistoryModal = ({ invoice, clients, settings, onClose, onEditPayment, onDeletePayment }) => {
  const [selectedPayment, setSelectedPayment] = useState(null);

  const formatCurrency = (amount) => `${settings.currency}${parseFloat(amount || 0).toFixed(2)}`;
  const formatDate = (date) => new Date(date).toLocaleDateString();
  const formatDateTime = (date) => new Date(date).toLocaleString();

  const client = clients.find(c => c._id === invoice.clientId);

  // Calculate invoice total
  const calculateInvoiceTotal = () => {
    const subtotal = invoice.items?.reduce((sum, item) => sum + (item.quantity * item.price), 0) || 0;
    const discount = subtotal * (invoice.discountPercentage || 0) / 100;
    const afterDiscount = subtotal - discount;
    const tax = settings.taxExempt ? 0 : afterDiscount * (settings.taxPercentage || 10) / 100;
    return afterDiscount + tax;
  };

  const invoiceTotal = calculateInvoiceTotal();
  const totalPaid = invoice.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
  const remainingAmount = invoiceTotal - totalPaid;

  const handleDeletePayment = (payment) => {
    if (window.confirm(`Are you sure you want to delete this payment of ${formatCurrency(payment.amount)}?`)) {
      onDeletePayment(payment._id);
    }
  };

  const exportPaymentHistory = () => {
    console.log('Exporting payment history...', {
      invoiceNumber: invoice.number,
      clientName: client?.name,
      totalPayments: invoice.payments?.length || 0,
      totalPaid: totalPaid
    });
    
    // In a real app, this would generate a CSV or PDF export
    alert('Payment history export functionality will be implemented with backend integration');
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Payment History - ${invoice.number}`}
      size="xlarge"
      footer={
        <div className="d-flex justify-content-between w-100">
          <div>
            {remainingAmount > 0 && (
              <span className="text-muted">
                Outstanding: <strong>{formatCurrency(remainingAmount)}</strong>
              </span>
            )}
          </div>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      <div className="p-0">
            {/* Invoice & Client Info */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Invoice Details</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-6">
                        <small className="text-muted">Invoice Number</small>
                        <div className="fw-bold">{invoice.number}</div>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Invoice Date</small>
                        <div>{formatDate(invoice.date)}</div>
                      </div>
                      <div className="col-6 mt-2">
                        <small className="text-muted">Due Date</small>
                        <div>{invoice.dueDate ? formatDate(invoice.dueDate) : 'Not set'}</div>
                      </div>
                      <div className="col-6 mt-2">
                        <small className="text-muted">Status</small>
                        <div>
                          <span className={`badge bg-${
                            remainingAmount <= 0 ? 'success' :
                            totalPaid > 0 ? 'warning' : 
                            invoice.status === 'overdue' ? 'danger' : 'secondary'
                          }`}>
                            {remainingAmount <= 0 ? 'Fully Paid' :
                             totalPaid > 0 ? 'Partially Paid' : 
                             invoice.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Payment Summary</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-6">
                        <small className="text-muted">Invoice Total</small>
                        <div className="fw-bold">{formatCurrency(invoiceTotal)}</div>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Total Paid</small>
                        <div className="fw-bold text-success">{formatCurrency(totalPaid)}</div>
                      </div>
                      <div className="col-6 mt-2">
                        <small className="text-muted">Remaining</small>
                        <div className={`fw-bold ${remainingAmount > 0 ? 'text-warning' : 'text-success'}`}>
                          {formatCurrency(remainingAmount)}
                        </div>
                      </div>
                      <div className="col-6 mt-2">
                        <small className="text-muted">Payment Count</small>
                        <div className="fw-bold">{invoice.payments?.length || 0}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0">Client Information</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <small className="text-muted">Client Name</small>
                    <div className="fw-medium">{client?.name || 'Unknown Client'}</div>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted">Email</small>
                    <div>{client?.email || 'Not provided'}</div>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted">Phone</small>
                    <div>{client?.phone || 'Not provided'}</div>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted">Payment Terms</small>
                    <div>{client?.paymentTerms || invoice.paymentTerms || 'Not set'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payments Table */}
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Payment History</h6>
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={exportPaymentHistory}
                >
                  <i className="fas fa-download me-1"></i>
                  Export
                </button>
              </div>
              <div className="card-body">
                {!invoice.payments || invoice.payments.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-money-bill fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Payments Recorded</h5>
                    <p className="text-muted">No payments have been recorded for this invoice yet.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Payment Date</th>
                          <th>Method</th>
                          <th>Reference</th>
                          <th className="text-end">Amount</th>
                          <th>Notes</th>
                          <th>Recorded</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.payments
                          .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate))
                          .map((payment, index) => (
                            <tr key={payment._id}>
                              <td className="fw-medium">
                                {formatDate(payment.paymentDate)}
                              </td>
                              <td>
                                <span className="badge bg-light text-dark">
                                  {payment.paymentMethod}
                                </span>
                              </td>
                              <td>
                                {payment.reference ? (
                                  <code className="small">{payment.reference}</code>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td className="text-end">
                                <span className="fw-bold text-success">
                                  {formatCurrency(payment.amount)}
                                </span>
                              </td>
                              <td>
                                {payment.notes ? (
                                  <span 
                                    className="text-truncate d-inline-block" 
                                    style={{maxWidth: '150px'}}
                                    title={payment.notes}
                                  >
                                    {payment.notes}
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                <small className="text-muted">
                                  {formatDateTime(payment.createdAt)}
                                </small>
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm">
                                  <button
                                    className="btn btn-outline-primary"
                                    onClick={() => setSelectedPayment(payment)}
                                    title="View Details"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-warning"
                                    onClick={() => onEditPayment && onEditPayment(payment)}
                                    title="Edit Payment"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDeletePayment(payment)}
                                    title="Delete Payment"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-active">
                          <th colSpan="3">Total Paid</th>
                          <th className="text-end">
                            <span className="fw-bold text-success">
                              {formatCurrency(totalPaid)}
                            </span>
                          </th>
                          <th colSpan="3"></th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Details Modal */}
            {selectedPayment && (
              <Modal
                isOpen={true}
                onClose={() => setSelectedPayment(null)}
                title="Payment Details"
                size="default"
                footer={
                  <div className="d-flex gap-3">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setSelectedPayment(null)}
                    >
                      Close
                    </button>
                    {onEditPayment && (
                      <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={() => {
                          onEditPayment(selectedPayment);
                          setSelectedPayment(null);
                        }}
                      >
                        Edit Payment
                      </button>
                    )}
                  </div>
                }
              >
                <div className="p-0">
                      <div className="row">
                        <div className="col-6 mb-3">
                          <label className="form-label text-muted">Amount</label>
                          <div className="fw-bold text-success h5">
                            {formatCurrency(selectedPayment.amount)}
                          </div>
                        </div>
                        <div className="col-6 mb-3">
                          <label className="form-label text-muted">Payment Date</label>
                          <div className="fw-medium">
                            {formatDate(selectedPayment.paymentDate)}
                          </div>
                        </div>
                        <div className="col-6 mb-3">
                          <label className="form-label text-muted">Payment Method</label>
                          <div>
                            <span className="badge bg-primary">
                              {selectedPayment.paymentMethod}
                            </span>
                          </div>
                        </div>
                        <div className="col-6 mb-3">
                          <label className="form-label text-muted">Reference</label>
                          <div className="fw-medium">
                            {selectedPayment.reference || 'Not provided'}
                          </div>
                        </div>
                        <div className="col-12 mb-3">
                          <label className="form-label text-muted">Notes</label>
                          <div className="border rounded p-2 bg-light">
                            {selectedPayment.notes || 'No notes provided'}
                          </div>
                        </div>
                        <div className="col-12">
                          <label className="form-label text-muted">Recorded At</label>
                          <div className="small text-muted">
                            {formatDateTime(selectedPayment.createdAt)}
                          </div>
                        </div>
                      </div>
                </div>
              </Modal>
            )}
      </div>
    </Modal>
  );
};

export default PaymentHistoryModal;